import * as React from 'react';
import { useFormik, FormikValues } from 'formik';
import { Constants, Validators } from 'core';
import { LanguageInfo, Reason, RequestError } from 'types';

import { useIntl } from 'common/config/i18n';
import FormError from 'components/molecules/form-error';
import { StyledRepositoryForm, StyledRepositoryFormButtonWrapper } from 'common/styling/mixins';
import Button from 'components/molecules/button';
import Input from 'components/organisms/input';

interface Props {
  onSubmit: (values: FormikValues) => void;
  reason?: Reason;
  translationLanguages: LanguageInfo[];
  error?: RequestError;
  status?: 'loading' | 'error' | 'success';
}

interface FormValues {
  id?: number;
  name: string;
  [languageCode: string]: string | number | undefined; // For dynamic translation language fields
}

const Form = ({ reason, translationLanguages, onSubmit, status, error }: Props) => {
  const { t } = useIntl();
  const isUpdate = !!reason;

  const getInitialValues = (): FormValues => {
    const baseValues: FormValues = {
      id: reason?.id,
      name: reason?.name || '',
    };

    // Add language-specific fields
    translationLanguages.forEach((lang) => {
      baseValues[lang.code] =
        reason?.translations.find((translation) => translation.languageCode === lang.code)?.name || '';
    });

    return baseValues;
  };

  const formik = useFormik({
    initialValues: getInitialValues(),
    validate: (values) => Validators.formReason(values, translationLanguages),
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  return (
    <StyledRepositoryForm onSubmit={formik.handleSubmit}>
      <div className="formSection">
        <Input
          upLabel={t('form', 'CHART_REASON')}
          name="name"
          onChange={formik.handleChange}
          value={formik.values.name}
          width="100%"
        />
        <FormError formik={formik} fieldName="name" />
      </div>

      {translationLanguages.map((lang) => (
        <div className="formSection" key={lang.code}>
          <Input
            upLabel={lang.label}
            name={lang.code}
            onChange={formik.handleChange}
            value={(formik.values[lang.code] as string) || ''}
            width="100%"
          />
          <FormError formik={formik} fieldName={lang.code} />
        </div>
      ))}

      <StyledRepositoryFormButtonWrapper>
        <Button
          height="35px"
          width="100%"
          borderRadius="20px"
          disabled={!formik.isValid && Object.keys(formik.touched).length !== 0}
          htmlType="submit"
          loading={status === 'loading'}
        >
          {t('Buttons', isUpdate ? 'SAVE_BUTTON' : 'CREATE_BUTTON')}
        </Button>
      </StyledRepositoryFormButtonWrapper>
      {error && (
        <FormError
          message={
            error === Constants.ALREADY_EXISTED_PROBLEM_CODE
              ? t('form', 'FORM_REASON_ALLREADY_EXIST')
              : t('form', 'FORM_UNKNOWN_ERROR')
          }
        />
      )}
    </StyledRepositoryForm>
  );
};
export default Form;
